import isEmpty from 'lodash/isEmpty';
import { redirect, replace } from 'redux-first-router';

import { ENABLE_EAGLE_PROJECT } from '@config';
import { payloadSel, querySel } from '@location';
import { unitsSearchRedirectRequest } from '@modules/councilUnits';
import { featureFlags } from '@modules/featureFlags/utils/featureFlags';
import { ROUTE_YOUTH_ADVANCEMENTS_LIST } from '@modules/shared/duck/actionsTyped';
import { emptyObj } from '@shared/constants';
import {
  ROUTE_ADVANCEMENT,
  ROUTE_PARENT_GUARDIAN_INVITATION,
  ROUTE_READ_ONLY_YOUTH_ACTIVITY_LOGS,
  ROUTE_READ_ONLY_YOUTH_PROFILE,
  isMeritBadgeCounselorSel,
} from '@shared/duck';
import { setOrganization } from '@shared/duck/actions';
import { organizationPositionsSel } from '@shared/duck/selectors/general.selectors';
import { newPermissions } from '@shared/newPermissions';

import {
  AdvRequirementsPage,
  AdvancementsListPage,
  EagleProjectPage,
  YouthActivityLogsPage,
  YouthProfilePage,
} from './components';
import ParentGuardianInvitationPage from './components/ParentGuardianInvitationPage';
import { advancementFilters } from './constants';
import {
  ROUTE_EAGLE_PROJECT,
  ROUTE_UNREGISTERED_YOUTH_PROFILE,
  ROUTE_YOUTH_ACTIVITY_LOGS,
  ROUTE_YOUTH_ADV_REQUIREMENTS,
  ROUTE_YOUTH_PROFILE,
  setAdvListFilter,
  setAdvListSearch,
  setAdvListSorter,
} from './duck/actions';
import {
  advListFilterSel,
  advListSearchSel,
  advListSorterSel,
  youthProfileInfoSel,
} from './duck/selectors';

const conditionalRoutes = {};

const baseRouteActivityLogs = {
  Component: YouthActivityLogsPage,
  titleId: 'page.youthActivityLogs',
};

if (
  featureFlags.getFlag('SHOW_YOUTH_ADV_REQUIREMENTS') ||
  featureFlags.getFlag('SBL_5746_ENABLE_ADV_BETA_UNITS') ||
  featureFlags.getFlag('SBL_5198_CUB_PROFILE_VIEWMORE')
) {
  conditionalRoutes[ROUTE_YOUTH_ADV_REQUIREMENTS] = {
    path: '/youthProfile/:userId/:advancementType/:advancementId',
    Component: AdvRequirementsPage,
    titleId: 'page.youthAdvRequirements',
    thunk: dispatch => dispatch({ type: '@@noop' }),
  };
}

if (ENABLE_EAGLE_PROJECT) {
  conditionalRoutes[ROUTE_EAGLE_PROJECT] = {
    path: '/youthProfile/:userId/eagleProject',
    Component: EagleProjectPage,
    titleId: 'page.eagleProject',
    thunk: (dispatch, getState) => {
      const { currentHighestRankApproved } = youthProfileInfoSel(getState());

      if (
        !currentHighestRankApproved ||
        currentHighestRankApproved.rank !== 'Life Scout'
      ) {
        dispatch(redirect({ type: ROUTE_ADVANCEMENT }));
      }
    },
    permissions: newPermissions.SUBMIT_EAGLE_PROJECT,
  };
}

const routes = {
  [ROUTE_YOUTH_PROFILE]: {
    path: '/youthProfile/:userId',
    Component: YouthProfilePage,
    titleId: 'page.youthProfile',
    thunk: (dispatch, getState) => {
      const state = getState();
      const positions = organizationPositionsSel(state);
      const {
        organizationGuid = '',
        adultUserId = '',
        section = '',
        councilUser = '',
        unitTypeId = '',
      } = querySel(state) || emptyObj;
      const payload = payloadSel(state);
      const connectionToYouthPosition = positions.find(
        position => +position.userId === +payload.userId,
      );
      const youthOrgGuid = organizationGuid;

      if (councilUser && organizationGuid && unitTypeId) {
        const { userId } = payload;
        replace(
          `/youthProfile/${userId}${
            councilUser ? `?councilUser=${councilUser}` : ''
          }${section ? `&section=${section}` : ''}`,
        );
        dispatch(
          unitsSearchRedirectRequest({
            orgGuid: organizationGuid,
            unitTypeId,
          }),
        );
        return;
      }

      // See SBL-4252
      if (
        isMeritBadgeCounselorSel(state) &&
        !youthOrgGuid &&
        connectionToYouthPosition
      ) {
        dispatch(
          setOrganization({
            guid: connectionToYouthPosition.organizationGuid,
            programType: connectionToYouthPosition.programType,
            parentOrgGuid: connectionToYouthPosition.organizationGuid,
          }),
        );
        return;
      }

      if (!isEmpty(youthOrgGuid) && !isEmpty(positions) && !isEmpty(payload)) {
        const { userId } = payload;
        const foundedPosition = positions.find(
          p => p.organizationGuid === youthOrgGuid,
        );
        replace(
          `/youthProfile/${userId}?section=${section}${
            adultUserId ? `&adultUserId=${adultUserId}` : ''
          }`,
        );
        if (foundedPosition) {
          dispatch(
            setOrganization({
              guid: foundedPosition.organizationGuid,
              programType: foundedPosition.programType,
              parentOrgGuid: foundedPosition.organizationGuid,
            }),
          );
        }
      }

      if (isEmpty(organizationGuid) && adultUserId && !isEmpty(payload)) {
        const { userId } = payload;
        replace(
          `/youthProfile/${userId}?adultUserId=${adultUserId}${
            section ? `&section=${section}` : ''
          }`,
        );
      }
    },
    permission: newPermissions.VIEW_YOUTH_PROFILE_PAGE,
  },
  [ROUTE_UNREGISTERED_YOUTH_PROFILE]: {
    path: '/youthProfile/:userId',
    Component: YouthProfilePage,
    titleId: 'page.youthProfile',
    thunk: (dispatch, getState) => {
      const state = getState();
      const payload = payloadSel(state);

      if (!isEmpty(payload)) {
        const { userId } = payload;
        replace(`/youthProfile/${userId}?adultUserId=424242`);
      }
    },
    permission: [
      newPermissions.VIEW_YOUTH_PROFILE_PAGE,
      newPermissions.VIEW_SELF_YOUTH_PROFILE,
    ],
  },
  [ROUTE_YOUTH_ADVANCEMENTS_LIST]: {
    path: '/youthProfile/:userId/:advancementType',
    Component: AdvancementsListPage,
    getTitleId: ({ advancementType }) => `page.youthAdvList.${advancementType}`,
    thunk: (dispatch, getState) => {
      const state = getState();
      const {
        sort = '',
        filter = '',
        search = '',
      } = querySel(state) || emptyObj;
      const currentFilter = advListFilterSel(state);
      const currentSorter = advListSorterSel(state);
      const currentSearch = advListSearchSel(state);

      const targetFilter = filter || advancementFilters.ALL;
      if (targetFilter !== currentFilter) {
        dispatch(setAdvListFilter(targetFilter));
      }

      if (sort && sort !== currentSorter) {
        dispatch(setAdvListSorter(sort));
      }

      if (search !== undefined && search !== currentSearch) {
        dispatch(setAdvListSearch(search));
      }
    },
  },
  [ROUTE_YOUTH_ACTIVITY_LOGS]: {
    path: '/youthProfile/:userId/activityLogs/:activityType',
    ...baseRouteActivityLogs,
  },
  [ROUTE_READ_ONLY_YOUTH_ACTIVITY_LOGS]: {
    path: '/youthProfile/activityLogs/:activityType',
    permission: [
      newPermissions.VIEW_YOUTH_PROFILE_PAGE,
      newPermissions.VIEW_SELF_YOUTH_PROFILE,
    ],
    ...baseRouteActivityLogs,
  },
  [ROUTE_READ_ONLY_YOUTH_PROFILE]: {
    path: '/youthProfile',
    Component: YouthProfilePage,
    titleId: 'page.youthProfile',
    permission: newPermissions.VIEW_SELF_YOUTH_PROFILE,
  },
  [ROUTE_PARENT_GUARDIAN_INVITATION]: {
    path: '/parentGuardianInvitation',
    Component: ParentGuardianInvitationPage,
    standalone: true,
    titleId: 'page.parentGuardianInvitation',
  },
  ...conditionalRoutes,
};

export default routes;
